import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '@/pages/app/components/AppLayout';
import { useAppAuth } from '@/hooks/useAppAuth';
import { createNotification } from '@/hooks/useNotifications';

type MatchInfo = {
  id: string;
  match_number: string | null;
  requester_id: string;
  receiver_id: string;
  requester_role: 'patient' | 'caregiver';
  receiver_role: 'patient' | 'caregiver';
  start_date: string | null;
  end_date: string | null;
  location: string | null;
  hourly_rate: number | null;
  requester?: { name: string } | null;
  receiver?: { name: string } | null;
};

export default function AppReviewWritePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const matchId = searchParams.get('match_id');
  const auth = useAppAuth(true);

  const [match, setMatch] = useState<MatchInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [existingReview, setExistingReview] = useState(false);

  useEffect(() => {
    if (!auth.userId || !matchId) return;
    loadMatch();
  }, [auth.userId, matchId]);

  async function loadMatch() {
    if (!matchId) return;
    setLoading(true);
    try {
      const { data, error: queryError } = await supabase
        .from('match_requests')
        .select('*')
        .eq('id', matchId)
        .eq('status', 'completed')
        .maybeSingle();

      if (queryError) throw queryError;
      if (!data) {
        setError('해당 매칭 정보를 찾을 수 없습니다.');
        setLoading(false);
        return;
      }

      const [reqResult, recResult] = await Promise.all([
        supabase.from('profiles').select('name').eq('id', data.requester_id).maybeSingle(),
        supabase.from('profiles').select('name').eq('id', data.receiver_id).maybeSingle(),
      ]);

      setMatch({
        ...data,
        requester: reqResult.data || null,
        receiver: recResult.data || null,
      });

      // 이미 리뷰 작성했는지 확인
      const { data: existing } = await supabase
        .from('reviews')
        .select('id')
        .eq('match_id', matchId)
        .eq('reviewer_id', auth.userId!)
        .maybeSingle();

      if (existing) setExistingReview(true);
    } catch (err) {
      console.error('Failed to load match:', err);
      setError('매칭 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }

  function getRevieweeInfo() {
    if (!match) return { name: '', role: '' };
    if (auth.userId === match.requester_id) {
      return {
        name: match.receiver?.name || '알 수 없음',
        role: match.receiver_role === 'caregiver' ? '간병인' : '환자/보호자',
      };
    }
    return {
      name: match.requester?.name || '알 수 없음',
      role: match.requester_role === 'caregiver' ? '간병인' : '환자/보호자',
    };
  }

  async function handleSubmit() {
    if (!match || !auth.userId) return;
    if (rating === 0) {
      setError('평점을 선택해주세요.');
      return;
    }
    setError('');
    setSubmitting(true);

    try {
      const revieweeId = auth.userId === match.requester_id
        ? match.receiver_id
        : match.requester_id;

      const { error: insertError } = await supabase.from('reviews').insert({
        match_id: match.id,
        reviewer_id: auth.userId,
        reviewee_id: revieweeId,
        rating,
        content: content || null,
      });

      if (insertError) throw insertError;

      // 간병인 리뷰인 경우 caregiver_profiles 평점 + 매칭횟수 자동 업데이트
      const revieweeRole = auth.userId === match.requester_id
        ? match.receiver_role
        : match.requester_role;

      if (revieweeRole === 'caregiver') {
        const { data: cgProfile } = await supabase
          .from('caregiver_profiles')
          .select('rating_avg, total_matches')
          .eq('user_id', revieweeId)
          .maybeSingle();

        const oldTotal = cgProfile?.total_matches || 0;
        const oldAvg = cgProfile?.rating_avg || 0;
        const newTotal = oldTotal + 1;
        const newAvg = Math.round(((oldAvg * oldTotal + rating) / newTotal) * 10) / 10;

        await supabase
          .from('caregiver_profiles')
          .update({
            rating_avg: newAvg,
            total_matches: newTotal,
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', revieweeId);

        // 간병인에게 리뷰 도착 알림
        createNotification({
          userId: revieweeId,
          type: 'review_received',
          title: '새로운 리뷰가 등록되었습니다',
          body: `${rating}점 리뷰가 도착했습니다.`,
          relatedId: match.id,
        });
      }

      setSuccess(true);
    } catch (err) {
      console.error('Failed to submit review:', err);
      setError('리뷰 등록 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  }

  if (auth.loading || loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  const reviewee = getRevieweeInfo();

  return (
    <AppLayout>
      <div className="min-h-screen px-5 pt-6 pb-24 bg-gray-50">
        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors">
            <i className="ri-arrow-left-line text-gray-500" />
          </button>
          <div>
            <h1 className="text-lg font-extrabold text-gray-900">리뷰 작성</h1>
            <p className="text-xs text-gray-400">간병 서비스에 대한 솔직한 평가를 남겨주세요</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-50 text-xs text-red-500 text-center">{error}</div>
        )}

        {existingReview ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-edit-2-line text-2xl text-amber-500" />
            </div>
            <h2 className="text-base font-extrabold text-gray-900 mb-2">이미 리뷰를 작성했어요</h2>
            <p className="text-sm text-gray-500">한 매칭당 한 번만 리뷰를 작성할 수 있습니다.</p>
            <button
              onClick={() => navigate('/app/matching')}
              className="mt-6 px-6 py-2.5 rounded-xl text-sm font-bold text-[#2E7D6B] bg-[#f0f9f6] cursor-pointer hover:opacity-80"
            >
              매칭 관리로 돌아가기
            </button>
          </div>
        ) : success ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-2xl bg-green-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-double-line text-2xl text-[#2E7D6B]" />
            </div>
            <h2 className="text-base font-extrabold text-gray-900 mb-2">리뷰가 등록되었습니다!</h2>
            <p className="text-sm text-gray-500 mb-8">소중한 평가 감사드립니다.</p>
            <div className="flex flex-col gap-2 max-w-xs mx-auto">
              <button
                onClick={() => navigate('/app/my-reviews')}
                className="w-full py-3 rounded-xl text-sm font-bold text-white cursor-pointer hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#2E7D6B' }}
              >
                <i className="ri-file-list-3-line mr-1.5" />
                내 리뷰 보기
              </button>
              <button
                onClick={() => navigate('/app/matching')}
                className="w-full py-3 rounded-xl text-sm font-bold text-gray-400 cursor-pointer hover:text-gray-600 hover:bg-gray-50 transition-colors"
              >
                매칭 관리로 돌아가기
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* 매칭 정보 */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${reviewee.role === '간병인' ? 'bg-[#E3F2FD]' : 'bg-[#E8F5E9]'}`}>
                  <i className={`text-lg ${reviewee.role === '간병인' ? 'ri-nurse-line text-[#1976D2]' : 'ri-hospital-line text-[#2E7D6B]'}`} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">{reviewee.name} {reviewee.role}</h3>
                  {match?.match_number && (
                    <p className="text-xs text-gray-400">매칭번호: {match.match_number}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {match?.location && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <i className="ri-map-pin-line text-gray-400" />
                    {match.location}
                  </p>
                )}
                {match?.start_date && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <i className="ri-calendar-line text-gray-400" />
                    {new Date(match.start_date).toLocaleDateString('ko-KR')} ~ {new Date(match.end_date || match.start_date).toLocaleDateString('ko-KR')}
                  </p>
                )}
                {match?.hourly_rate != null && match.hourly_rate > 0 && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <i className="ri-money-dollar-circle-line text-gray-400" />
                    {match.hourly_rate.toLocaleString()}원/시간
                  </p>
                )}
              </div>
            </div>

            {/* 별점 */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5">
              <h3 className="text-sm font-extrabold text-gray-900 mb-4">
                평점을 선택해주세요 <span className="text-red-400">*</span>
              </h3>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="cursor-pointer transition-transform hover:scale-110"
                  >
                    <i
                      className={`text-3xl ${
                        star <= (hoverRating || rating)
                          ? 'ri-star-fill text-amber-400'
                          : 'ri-star-line text-gray-300'
                      }`}
                    />
                  </button>
                ))}
                <span className="ml-3 text-sm font-bold text-gray-900">
                  {rating > 0 ? `${rating}점` : '선택 안 함'}
                </span>
              </div>
            </div>

            {/* 리뷰 내용 */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5">
              <h3 className="text-sm font-extrabold text-gray-900 mb-3">
                상세 리뷰 <span className="text-gray-400 font-normal text-xs">(선택)</span>
              </h3>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value.slice(0, 500))}
                placeholder="간병 서비스에 대한 경험을 공유해주세요. 다른 분들에게 큰 도움이 됩니다."
                rows={5}
                maxLength={500}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white resize-none"
              />
              <p className="text-xs text-gray-400 text-right mt-1">{content.length}/500</p>
            </div>

            {/* 제출 버튼 */}
            <button
              onClick={handleSubmit}
              disabled={submitting || rating === 0}
              className="w-full py-3.5 rounded-xl text-sm font-bold text-white cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />등록 중...
                </span>
              ) : (
                '리뷰 등록하기'
              )}
            </button>
          </>
        )}
      </div>
    </AppLayout>
  );
}